import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ConciliacionBancaria, ConciliarRequest } from '../types/conciliacion';
import { ConciliacionBancariaService } from '../services/conciliacionService';

interface ConciliacionState {
  conciliacion: ConciliacionBancaria | null;
  loading: boolean;
  error: string | null;
  selectedIds: number[];

  fetchConciliacion: (idCajaBanco: number, ejercicio: string, periodo: string) => Promise<void>;
  conciliar: (request: ConciliarRequest) => Promise<void>;
  desconciliar: (ids: number[]) => Promise<void>;
  aplicar: (id: number) => Promise<void>;
  toggleSelected: (id: number) => void;
  selectAll: () => void;
  clearSelection: () => void;
  clearError: () => void;
}

export const useConciliacionStore = create<ConciliacionState>()(
  devtools(
    (set, get) => ({
      conciliacion: null,
      loading: false,
      error: null,
      selectedIds: [],

      fetchConciliacion: async (idCajaBanco, ejercicio, periodo) => {
        set({ loading: true, error: null }, false, 'conciliacion/fetch/start');
        try {
          const conciliacion = await ConciliacionBancariaService.getConciliacion(idCajaBanco, ejercicio, periodo);
          set({ conciliacion, loading: false, selectedIds: [] }, false, 'conciliacion/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar conciliacion';
          set({ loading: false, error: errorMessage }, false, 'conciliacion/fetch/error');
        }
      },

      conciliar: async (request) => {
        set({ loading: true, error: null }, false, 'conciliacion/conciliar/start');
        try {
          await ConciliacionBancariaService.conciliar(request);
          const { conciliacion } = get();
          if (conciliacion) {
            await get().fetchConciliacion(conciliacion.idCajaBanco, conciliacion.ejercicio, conciliacion.periodo);
          }
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al conciliar';
          set({ loading: false, error: errorMessage }, false, 'conciliacion/conciliar/error');
          throw error;
        }
      },

      desconciliar: async (ids) => {
        set({ loading: true, error: null }, false, 'conciliacion/desconciliar/start');
        try {
          await ConciliacionBancariaService.desconciliar(ids);
          const { conciliacion } = get();
          if (conciliacion) {
            await get().fetchConciliacion(conciliacion.idCajaBanco, conciliacion.ejercicio, conciliacion.periodo);
          }
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al desconciliar';
          set({ loading: false, error: errorMessage }, false, 'conciliacion/desconciliar/error');
          throw error;
        }
      },

      aplicar: async (id) => {
        set({ loading: true, error: null }, false, 'conciliacion/aplicar/start');
        try {
          await ConciliacionBancariaService.aplicar(id);
          set((state) => ({
            conciliacion: state.conciliacion ? { ...state.conciliacion, estado: 'APLICADO' } : null,
            loading: false,
          }), false, 'conciliacion/aplicar/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al aplicar conciliacion';
          set({ loading: false, error: errorMessage }, false, 'conciliacion/aplicar/error');
          throw error;
        }
      },

      toggleSelected: (id) => set((state) => ({
        selectedIds: state.selectedIds.includes(id)
          ? state.selectedIds.filter(i => i !== id)
          : [...state.selectedIds, id],
      }), false, 'conciliacion/toggleSelected'),

      selectAll: () => set((state) => ({
        selectedIds: state.conciliacion?.detalles
          .filter(d => !d.esConciliado)
          .map(d => d.idMovimientoTesoreria) || [],
      }), false, 'conciliacion/selectAll'),

      clearSelection: () => set({ selectedIds: [] }, false, 'conciliacion/clearSelection'),
      clearError: () => set({ error: null }, false, 'conciliacion/clearError'),
    }),
    { name: 'ConciliacionStore' }
  )
);
